import React, { useState } from 'react';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import '../OnboardingWizard.css';

const CAREER_STATUSES = [
  { id: 'student', label: 'Student' },
  { id: 'recent_grad', label: 'Recent Graduate' },
  { id: 'employed', label: 'Currently Employed' },
  { id: 'between_roles', label: 'Between Roles' },
];

const TARGET_ROLES = [
  'Backend Developer',
  'Frontend Developer',
  'Full Stack Developer',
  'DevOps Engineer',
  'Data Scientist',
  'Machine Learning Engineer',
  'iOS Developer',
  'Android Developer',
  'Software Engineer',
  'Site Reliability Engineer',
  'Cloud Engineer',
  'Security Engineer',
  'QA Engineer',
  'Product Manager',
  'Engineering Manager',
];

const MAX_LOCATIONS = 5;

const REMOTE_PREFERENCES = [
  { id: 'remote_only', label: 'Remote Only' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'on_site', label: 'On-site' },
  { id: 'flexible', label: 'Flexible' },
];

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Generate years from current year to 10 years in the future
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 11 }, (_, i) => currentYear + i);

function CareerProfile() {
  const { data, updateStepData } = useOnboarding();
  const [roleSearch, setRoleSearch] = useState('');
  const [locationInput, setLocationInput] = useState('');

  // Parse graduation_date (format: "YYYY-MM") into month and year
  const gradMonth = data.graduation_date ? data.graduation_date.split('-')[1] : '';
  const gradYear = data.graduation_date ? data.graduation_date.split('-')[0] : '';

  const handleGradMonthChange = (month) => {
    const year = gradYear || String(currentYear);
    if (month) {
      updateStepData({ graduation_date: `${year}-${month}` });
    } else if (!gradYear) {
      updateStepData({ graduation_date: null });
    }
  };

  const handleGradYearChange = (year) => {
    const month = gradMonth || '05'; // Default to May if no month selected
    if (year) {
      updateStepData({ graduation_date: `${year}-${month}` });
    } else if (!gradMonth) {
      updateStepData({ graduation_date: null });
    }
  };

  const showGraduationDate = data.career_status === 'student';
  const showYearsExperience = ['employed', 'recent_grad', 'between_roles'].includes(data.career_status);

  const filteredRoles = TARGET_ROLES.filter(
    role => role.toLowerCase().includes(roleSearch.toLowerCase()) &&
            !data.target_roles?.includes(role)
  );

  const handleAddRole = (role) => {
    if (data.target_roles?.length < 5) {
      updateStepData({ target_roles: [...(data.target_roles || []), role] });
      setRoleSearch('');
    }
  };

  const handleRemoveRole = (role) => {
    updateStepData({ target_roles: data.target_roles?.filter(r => r !== role) || [] });
  };

  const handleAddLocation = () => {
    const trimmed = locationInput.trim();
    if (trimmed && (data.preferred_locations?.length || 0) < MAX_LOCATIONS && !data.preferred_locations?.includes(trimmed)) {
      updateStepData({ preferred_locations: [...(data.preferred_locations || []), trimmed] });
      setLocationInput('');
    }
  };

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLocation();
    }
  };

  const handleRemoveLocation = (location) => {
    updateStepData({ preferred_locations: data.preferred_locations?.filter(l => l !== location) || [] });
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h1 className="step-title">Tell us about your career</h1>
        <p className="step-subtitle">
          This helps us find the right opportunities for you.
        </p>
      </div>

      <div className="form-grid">
        {/* Career Status */}
        <div className="form-group">
          <label className="form-label">Current Status</label>
          <div className="radio-group">
            {CAREER_STATUSES.map((status) => (
              <label
                key={status.id}
                className={`radio-card ${data.career_status === status.id ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="career_status"
                  value={status.id}
                  checked={data.career_status === status.id}
                  onChange={(e) => updateStepData({ career_status: e.target.value })}
                />
                <span>{status.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Graduation Date (conditional) */}
        {showGraduationDate && (
          <div className="form-group">
            <label className="form-label">Expected Graduation</label>
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <select
                className="form-select"
                value={gradMonth}
                onChange={(e) => handleGradMonthChange(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                className="form-select"
                value={gradYear}
                onChange={(e) => handleGradYearChange(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Year</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Years of Experience (conditional) */}
        {showYearsExperience && (
          <div className="form-group">
            <label className="form-label">Years of Experience</label>
            <select
              className="form-select"
              value={data.years_experience ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                updateStepData({ years_experience: val === '' ? null : parseInt(val) });
              }}
            >
              <option value="">Select...</option>
              <option value="0">Less than 1 year</option>
              <option value="1">1 year</option>
              <option value="2">2 years</option>
              <option value="3">3 years</option>
              <option value="4">4 years</option>
              <option value="5">5 years</option>
              <option value="6">6-8 years</option>
              <option value="9">9-12 years</option>
              <option value="13">13+ years</option>
            </select>
          </div>
        )}

        {/* Target Roles */}
        <div className="form-group">
          <label className="form-label">
            Target Roles <span className="label-hint">(select up to 5)</span>
          </label>
          <div className="tags-input-container">
            <div className="selected-tags">
              {data.target_roles?.map(role => (
                <span key={role} className="tag">
                  {role}
                  <button type="button" onClick={() => handleRemoveRole(role)} className="tag-remove">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </span>
              ))}
            </div>
            {(data.target_roles?.length || 0) < 5 && (
              <div className="search-dropdown">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search roles..."
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                />
                {roleSearch && filteredRoles.length > 0 && (
                  <ul className="dropdown-list">
                    {filteredRoles.slice(0, 6).map(role => (
                      <li key={role} onClick={() => handleAddRole(role)}>
                        {role}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Preferred Locations */}
        <div className="form-group">
          <label className="form-label">
            Preferred Locations <span className="label-hint">(up to {MAX_LOCATIONS} cities or zip codes)</span>
          </label>
          <div className="tags-input-container">
            <div className="selected-tags">
              {data.preferred_locations?.map(location => (
                <span key={location} className="tag secondary">
                  {location}
                  <button type="button" onClick={() => handleRemoveLocation(location)} className="tag-remove">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </span>
              ))}
            </div>
            {(data.preferred_locations?.length || 0) < MAX_LOCATIONS && (
              <div className="location-input-row">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter city, state or zip code"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={handleLocationKeyDown}
                />
                <button
                  type="button"
                  className="btn primary"
                  onClick={handleAddLocation}
                  disabled={!locationInput.trim()}
                  style={{ padding: 'var(--space-sm) var(--space-lg)', whiteSpace: 'nowrap' }}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Remote Preference */}
        <div className="form-group">
          <label className="form-label">Remote Preference</label>
          <div className="radio-group horizontal">
            {REMOTE_PREFERENCES.map((pref) => (
              <label
                key={pref.id}
                className={`radio-card small ${data.remote_preference === pref.id ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="remote_preference"
                  value={pref.id}
                  checked={data.remote_preference === pref.id}
                  onChange={(e) => updateStepData({ remote_preference: e.target.value })}
                />
                <span>{pref.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CareerProfile;
